import { useEffect, useRef } from 'react';

import { fabric } from 'fabric';

import { calculateZoom } from '~/contexts/SidePanelContext';

import { IUseFabric } from './types';

/**
 * @function
 * Add zoom to canvas
 * @see {@link https://github.com/fabricjs/fabric.js/discussions/7052}
 * @see {@link https://jsfiddle.net/j1vdo6c5/6/}
 */
export const useZoom = (props: IUseFabric) => {
  const { ref, context } = props;
  const { annotationEnabled, zoom, minZoom, maxZoom, update } = context;

  // keeps zoom constrained within background image 
  const enclose = useRef(() => {
    const canvas = ref.current;
    const image = canvas.backgroundImage as fabric.Image;
    const { br: brRaw, tl: tlRaw } = image?.aCoords || {};
    const vt = canvas.viewportTransform || [];
    if (brRaw && tlRaw) {
      const br = fabric.util.transformPoint(brRaw, vt);
      const tl = fabric.util.transformPoint(tlRaw, vt);
      const { x: left, y: top } = tl;
      const { x: right, y: bottom } = br;
      const width = canvas.getWidth();
      const height = canvas.getHeight();
      // calculate how far to translate to line up the edge of the object with  
      // the edge of the canvas                                                 
      const dLeft = Math.abs(right - width);
      const dRight = Math.abs(left);
      const dUp = Math.abs(bottom - height);
      const dDown = Math.abs(top);
      // if the object is larger than the canvas, clamp translation such that   
      // we don't push the opposite boundary past the edge                      
      const maxDx = Math.min(dLeft, dRight);
      const maxDy = Math.min(dUp, dDown);
      const leftIsOver = left < 0;
      const rightIsOver = right > width;
      const topIsOver = top < 0;
      const bottomIsOver = bottom > height;
      const translateLeft = rightIsOver && !leftIsOver;
      const translateRight = leftIsOver && !rightIsOver;
      const translateUp = bottomIsOver && !topIsOver;
      const translateDown = topIsOver && !bottomIsOver;
      const dx = translateLeft ? -maxDx : translateRight ? maxDx : 0;
      const dy = translateUp ? -maxDy : translateDown ? maxDy : 0;
      if (dx || dy) {
        vt[4] += dx;
        vt[5] += dy;
        canvas.requestRenderAll();
      }
    }
  }).current;

  // set zoom on mouse wheel
  useEffect(() => {
    const canvas = ref.current;
    let isDragging = false;
    let lastPosX = 0;
    let lastPosY = 0;

    const handleMouseWheel = (opt: fabric.IEvent<WheelEvent>) => {
      const { e: event } = opt;
      event.preventDefault();
      event.stopPropagation();
      const point = { x: event.offsetX, y: event.offsetY };
      const zoom = calculateZoom(minZoom, maxZoom, canvas.getZoom(), event.deltaY);
      canvas.zoomToPoint(point, zoom);
      update({ zoom });
      enclose();
    }

    const handleMouseDown = (opt: fabric.IEvent<MouseEvent>) => {
      const { e: event } = opt;
      const { clientX, clientY } = event;
      const zoom = canvas.getZoom();
      if (zoom > 1) {
        isDragging = true;
        lastPosX = clientX;
        lastPosY = clientY;
        canvas.discardActiveObject();
      }
    }

    const handleMouseMove = (opt: fabric.IEvent<MouseEvent>) => {
      const { e: event } = opt;
      const { clientX, clientY } = event;
      if (isDragging) {
        event.stopPropagation();
        event.preventDefault();
        const vt = canvas.viewportTransform || [];
        vt[4] += clientX - lastPosX;
        vt[5] += clientY - lastPosY;
        canvas.requestRenderAll();
        lastPosX = clientX;
        lastPosY = clientY;
        enclose();
      }
    }

    const handleMouseUp = () => {
      canvas.setViewportTransform(canvas.viewportTransform || []);
      isDragging = false;
    }

    if (!annotationEnabled) {
      canvas.on('mouse:down', handleMouseDown);
      canvas.on('mouse:move', handleMouseMove);
      canvas.on('mouse:up', handleMouseUp);
      canvas.on('mouse:wheel', handleMouseWheel)
      return () => {
        canvas.off('mouse:down', handleMouseDown);
        canvas.off('mouse:move', handleMouseMove);
        canvas.off('mouse:up', handleMouseUp);
        canvas.off('mouse:wheel', handleMouseWheel)
      }
    }
  }, [annotationEnabled, enclose, maxZoom, minZoom, ref, update]);

  // set zoom when changes zoom level by UI elements. [+ % -] elements
  useEffect(() => {
    const canvas = ref.current;
    if (canvas.getZoom() !== zoom) {
      const point = new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2)
      canvas.zoomToPoint(point, zoom);
      enclose();
    }
  }, [zoom, ref, enclose]);

  useEffect(() => {
    window.addEventListener('resize', enclose);
    return () => {
      window.removeEventListener('resize', enclose);
    };
  }, [enclose]);
}
